import { useEffect, useState, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiUsers, FiRefreshCw, FiTrash2, FiActivity, FiLayers, FiCpu, FiX, FiShield, FiChevronLeft, FiChevronRight, FiEdit2, FiPause, FiPlay, FiSettings, FiChevronDown, FiUser, FiHelpCircle } from 'react-icons/fi';
import { Button } from '../components/Button';
import { CustomNameModal } from '../components/CustomNameModal';
import { PageLoading, CardLoading } from '../components/LoadingSpinner';
import { getUserCopyConfigs, getCopyConfigDetail, getCopyConfigTrades, toggleConfigAutoCopy, deleteCopyConfig, updateCopyGlobalRisk, pauseCopyGlobalRisk, resumeCopyGlobalRisk, updateCopyConfigRisk } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { useI18n } from '../i18n/useI18n';

const DEFAULT_MARKET_COOLDOWN_MINUTES = 3;
const MIN_MARKET_COOLDOWN_MINUTES = 1;
const MAX_MARKET_COOLDOWN_MINUTES = 30;

export default function MyCopyTrading({ onRequireMembership = () => {} }) {
  const { userId } = useAuth();
  const { pushToast } = useToast();
  const { t, lang } = useI18n();

  const [configs, setConfigs] = useState([]);
  const [domainLabels, setDomainLabels] = useState({});
  const [globalSettings, setGlobalSettings] = useState({
    copy_global_loss_limit: null,
    copy_global_paused: false,
    copy_global_paused_at: null,
    copy_global_pause_reason: null,
    copy_global_pause_reason_type: null,
    copy_global_last_loss: null,
    copy_global_insufficient_balance: false,
    copy_global_last_balance: null,
    copy_global_required_balance: null,
  });
  const [stats, setStats] = useState({
    total_configs: 0,
    active_auto_copy: 0,
    total_trades: 0,
    total_profit: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [selectedConfigDetail, setSelectedConfigDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [membershipRequired, setMembershipRequired] = useState(false);

  const [trades, setTrades] = useState([]);
  const [tradesPage, setTradesPage] = useState(1);
  const [tradesTotalPages, setTradesTotalPages] = useState(0);
  const [tradesTotal, setTradesTotal] = useState(0);
  const [tradesLoading, setTradesLoading] = useState(false);

  const TRADES_PAGE_SIZE = 10;
  const [refreshing, setRefreshing] = useState(false);
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);
  const [customNameTarget, setCustomNameTarget] = useState(null);

  const [globalRiskOpen, setGlobalRiskOpen] = useState(false);
  const [globalLossLimitInput, setGlobalLossLimitInput] = useState('');
  const [strategyRiskOpen, setStrategyRiskOpen] = useState(false);
  const [strategyInvestmentLimitInput, setStrategyInvestmentLimitInput] = useState('');
  const [strategyStopLossEnabled, setStrategyStopLossEnabled] = useState(false);
  const [strategyStopLossLimitInput, setStrategyStopLossLimitInput] = useState('');
  const [strategyMarketCooldownEnabled, setStrategyMarketCooldownEnabled] = useState(false);
  const [strategyMarketCooldownMinutesInput, setStrategyMarketCooldownMinutesInput] = useState(
    String(DEFAULT_MARKET_COOLDOWN_MINUTES)
  );
  const [tradersListOpen, setTradersListOpen] = useState(false);

  useEffect(() => {
    const shouldLock = globalRiskOpen || strategyRiskOpen;
    if (!shouldLock) return;

    const scrollY = window.scrollY || 0;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyWidth = document.body.style.width;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setGlobalRiskOpen(false);
        setStrategyRiskOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [globalRiskOpen, strategyRiskOpen]);

  const sortedConfigs = useMemo(() => {
    return [...configs].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [configs]);

  useEffect(() => {
    if (userId) {
      loadConfigs();
    } else {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    const handleMembershipUpdated = () => {
      setMembershipRequired(false);
      loadConfigs();
    };
    window.addEventListener('membership-updated', handleMembershipUpdated);
    return () => {
      window.removeEventListener('membership-updated', handleMembershipUpdated);
    };
  }, [userId]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      setMembershipRequired(false);
      const response = await getUserCopyConfigs(userId);

      if (response.code === 0) {
        const loadedConfigs = response.data.configs || [];
        setConfigs(loadedConfigs);
        setStats(response.data.stats || {});
        setDomainLabels(response.data.domain_labels || {});
        setGlobalSettings(response.data.global_settings || {
          copy_global_loss_limit: null,
          copy_global_paused: false,
          copy_global_paused_at: null,
          copy_global_pause_reason: null,
          copy_global_pause_reason_type: null,
          copy_global_last_loss: null,
          copy_global_insufficient_balance: false,
          copy_global_last_balance: null,
          copy_global_required_balance: null,
        });

        setSelectedConfig((prev) => {
          if (!loadedConfigs || loadedConfigs.length === 0) return null;

          const sorted = [...loadedConfigs].sort((a, b) => {
            const dateA = new Date(a.created_at || 0).getTime();
            const dateB = new Date(b.created_at || 0).getTime();
            return dateB - dateA;
          });

          if (!prev) {
            const latestConfig = sorted[0];
            loadConfigDetail(latestConfig.id);
            return latestConfig;
          }
          const matched = loadedConfigs.find((c) => c.id === prev.id);
          if (matched) {
            loadConfigDetail(matched.id);
          }
          return matched || loadedConfigs[0];
        });
      } else {
        if (userId) pushToast(t('copyTrading.loadFailed'), 'error');
      }
    } catch (error) {
      console.error('Load configs failed:', error);
      if (error?.isMembershipRequired) {
        setMembershipRequired(true);
      } else if (userId) {
        pushToast(t('copyTrading.loadFailed'), 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadConfigs();
    setRefreshing(false);
    pushToast(t('copyTrading.refreshed'), 'success');
  };

  const handleOpenCustomName = useCallback((config) => {
    if (!config) return;
    setCustomNameTarget(config);
  }, []);

  const handleCustomNameSaved = useCallback(
    (customName) => {
      if (!customNameTarget?.id) return;
      const targetId = customNameTarget.id;
      const nextValue = customName || null;
      setConfigs((prev) =>
        prev.map((cfg) =>
          cfg.id === targetId ? { ...cfg, custom_name: nextValue } : cfg
        )
      );
      setSelectedConfig((prev) =>
        prev?.id === targetId ? { ...prev, custom_name: nextValue } : prev
      );
      setSelectedConfigDetail((prev) =>
        prev?.id === targetId ? { ...prev, custom_name: nextValue } : prev
      );
      setCustomNameTarget((prev) => (prev ? { ...prev, custom_name: nextValue } : prev));
    },
    [customNameTarget]
  );

  const loadConfigDetail = useCallback(async (configId) => {
    if (!configId) return;

    setDetailLoading(true);
    setTrades([]);
    setTradesPage(1);
    setTradesTotalPages(0);
    setTradesTotal(0);

    try {
      const response = await getCopyConfigDetail(configId);
      if (response.code === 0) {
        setSelectedConfigDetail(response.data);
        loadTrades(configId, 1);
      } else {
        console.error('Load config detail failed:', response.msg);
        setSelectedConfigDetail(null);
      }
    } catch (error) {
      console.error('Load config detail failed:', error);
      if (error?.isMembershipRequired) {
        setMembershipRequired(true);
      }
      setSelectedConfigDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const loadTrades = useCallback(async (configId, page) => {
    if (!configId || !userId) return;

    setTradesLoading(true);
    try {
      const response = await getCopyConfigTrades(userId, configId, page, TRADES_PAGE_SIZE);
      if (response.code === 0) {
        setTrades(response.data.items || []);
        setTradesPage(response.data.page || 1);
        setTradesTotalPages(response.data.total_pages || 0);
        setTradesTotal(response.data.total || 0);
      } else {
        console.error('Load trades failed:', response.msg);
        pushToast(t('copyTrading.loadTradesFailed'), 'error');
      }
    } catch (error) {
      console.error('Load trades failed:', error);
      if (error?.isMembershipRequired) {
        setMembershipRequired(true);
      }
    } finally {
      setTradesLoading(false);
    }
  }, [userId, TRADES_PAGE_SIZE]);

  const handleTradesPageChange = useCallback((newPage) => {
    if (selectedConfig?.id) {
      loadTrades(selectedConfig.id, newPage);
    }
  }, [selectedConfig, loadTrades]);

  const handleConfigSelect = (config) => {
    setSelectedConfig(config);
    setSelectedConfigDetail(null);
    loadConfigDetail(config.id);
    if (window.innerWidth < 1024) {
      setIsMobileDetailOpen(true);
    }
  };

  const handleCloseMobileDetail = () => {
    setIsMobileDetailOpen(false);
  };

  const openGlobalRiskModal = () => {
    const val = globalSettings?.copy_global_loss_limit;
    setGlobalLossLimitInput(val ? String(val) : '');
    setGlobalRiskOpen(true);
  };

  const saveGlobalRisk = async () => {
    try {
      const raw = (globalLossLimitInput || '').trim();
      const value = raw === '' ? null : Number(raw);
      if (raw !== '' && (!Number.isFinite(value) || value <= 0)) {
        pushToast(t('copyTrading.invalidLossLimit'), 'error');
        return;
      }

      await updateCopyGlobalRisk(userId, value);
      pushToast(t('copyTrading.globalRiskUpdated'), 'success');
      setGlobalRiskOpen(false);
      await loadConfigs();
    } catch (e) {
      console.error('Update global risk failed:', e);
      pushToast(t('copyTrading.globalRiskUpdateFailed'), 'error');
    }
  };

  const handlePauseGlobal = async () => {
    if (!confirm(t('copyTrading.pauseGlobalConfirm'))) return;
    try {
      await pauseCopyGlobalRisk(userId);
      pushToast(t('copyTrading.pauseGlobalOk'), 'success');
      await loadConfigs();
    } catch (e) {
      console.error('Pause global copy failed:', e);
      pushToast(t('copyTrading.pauseGlobalFailed'), 'error');
    }
  };

  const handleResumeGlobal = async () => {
    try {
      await resumeCopyGlobalRisk(userId);
      pushToast(t('copyTrading.resumeGlobalOk'), 'success');
      await loadConfigs();
    } catch (e) {
      console.error('Resume global copy failed:', e);
      pushToast(t('copyTrading.resumeFailed'), 'error');
    }
  };

  const openStrategyRiskModal = (config) => {
    if (config?.id) setSelectedConfig(config);
    const inv = config?.investment_total_limit;
    setStrategyInvestmentLimitInput(inv ? String(inv) : '');
    setStrategyStopLossEnabled(!!config?.stop_loss_enabled);
    const limit = config?.stop_loss_limit;
    setStrategyStopLossLimitInput(limit ? String(limit) : '');
    setStrategyMarketCooldownEnabled(!!config?.market_cooldown_enabled);
    const cooldownMinutes = config?.market_cooldown_minutes;
    setStrategyMarketCooldownMinutesInput(
      cooldownMinutes != null ? String(cooldownMinutes) : String(DEFAULT_MARKET_COOLDOWN_MINUTES)
    );
    setStrategyRiskOpen(true);
  };

  const saveStrategyRisk = async () => {
    if (!selectedConfig?.id) return;

    try {
      const invRaw = (strategyInvestmentLimitInput || '').trim();
      const investment_total_limit = invRaw === '' ? null : Number(invRaw);
      if (invRaw !== '' && (!Number.isFinite(investment_total_limit) || investment_total_limit <= 0)) {
        pushToast(t('copyTrading.invalidInvestmentLimit'), 'error');
        return;
      }

      let stop_loss_limit = undefined;
      if (strategyStopLossEnabled) {
        const limitRaw = (strategyStopLossLimitInput || '').trim();
        if (limitRaw === '') {
          stop_loss_limit = null;
        } else {
          const limitVal = Number(limitRaw);
          if (!Number.isFinite(limitVal) || limitVal < 1) {
            pushToast(t('copyTrading.invalidStopLossLimit'), 'error');
            return;
          }
          stop_loss_limit = limitVal;
        }
      }

      let market_cooldown_minutes = undefined;
      if (strategyMarketCooldownEnabled) {
        const cooldownRaw = (strategyMarketCooldownMinutesInput || '').trim();
        if (cooldownRaw === '') {
          market_cooldown_minutes = null;
        } else {
          const minutesVal = Number(cooldownRaw);
          if (
            !Number.isInteger(minutesVal)
            || minutesVal < MIN_MARKET_COOLDOWN_MINUTES
            || minutesVal > MAX_MARKET_COOLDOWN_MINUTES
          ) {
            pushToast(
              t('copyTrading.invalidMarketCooldownMinutes', {
                min: MIN_MARKET_COOLDOWN_MINUTES,
                max: MAX_MARKET_COOLDOWN_MINUTES,
              }),
              'error'
            );
            return;
          }
          market_cooldown_minutes = minutesVal;
        }
      }

      await updateCopyConfigRisk(selectedConfig.id, {
        investment_total_limit,
        stop_loss_enabled: strategyStopLossEnabled,
        ...(stop_loss_limit !== undefined ? { stop_loss_limit } : {}),
        market_cooldown_enabled: strategyMarketCooldownEnabled,
        ...(market_cooldown_minutes !== undefined ? { market_cooldown_minutes } : {}),
      });
      pushToast(t('copyTrading.strategyRiskUpdated'), 'success');
      setStrategyRiskOpen(false);
      await loadConfigs();
    } catch (e) {
      console.error('Update strategy risk failed:', e);
      pushToast(t('copyTrading.strategyRiskUpdateFailed'), 'error');
    }
  };

  const handleToggleAutoCopy = async (configId) => {
    try {
      const response = await toggleConfigAutoCopy(configId);

      if (response.code === 0) {
        const updatedConfigs = configs.map(c =>
          c.id === configId
            ? { ...c, auto_copy_enabled: response.data.auto_copy_enabled }
            : c
        );
        setConfigs(updatedConfigs);
        
        if (selectedConfig && selectedConfig.id === configId) {
            setSelectedConfig({ ...selectedConfig, auto_copy_enabled: response.data.auto_copy_enabled });
        }

        pushToast(
          response.data.auto_copy_enabled ? t('copyTrading.autoCopyOn') : t('copyTrading.autoCopyOff'),
          'success'
        );
        
        setStats(prev => ({
            ...prev,
            active_auto_copy: response.data.auto_copy_enabled 
                ? prev.active_auto_copy + 1 
                : Math.max(0, prev.active_auto_copy - 1)
        }));

      } else {
        pushToast(t('copyTrading.operationFailed'), 'error');
      }
    } catch (error) {
      console.error('Toggle failed:', error);
      pushToast(t('copyTrading.operationFailed'), 'error');
    }
  };

  const handleDelete = async (configId) => {
    if (!confirm(t('copyTrading.deleteConfirm'))) return;

    try {
      const response = await deleteCopyConfig(configId);

      if (response.code === 0) {
        const newConfigs = configs.filter(c => c.id !== configId);
        setConfigs(newConfigs);
        setSelectedConfig(newConfigs.length > 0 ? newConfigs[0] : null);
        pushToast(t('copyTrading.deletedOk'), 'success');

        await loadConfigs();
        
        if (isMobileDetailOpen && selectedConfig?.id === configId) {
            setIsMobileDetailOpen(false);
        }
      } else {
        pushToast(t('copyTrading.deleteFailed'), 'error');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      pushToast(t('copyTrading.deleteFailed'), 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const raw = String(dateString).trim();
    if (!raw) return '-';

    const hasTimezone =
      /[zZ]$/.test(raw) ||
      /[+-]\d{2}:?\d{2}$/.test(raw) ||
      /[+-]\d{2}:?\d{2}\s*$/.test(raw);

    const normalized = hasTimezone
      ? raw
      : raw.replace(' ', 'T') + 'Z';

    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return '-';

    const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    let date;
    if (typeof timestamp === 'number') {
      date = new Date(timestamp * 1000);
    } else {
      const raw = String(timestamp).trim();
      const hasTimezone =
        /[zZ]$/.test(raw) ||
        /[+-]\d{2}:?\d{2}$/.test(raw) ||
        /[+-]\d{2}:?\d{2}\s*$/.test(raw);
      const normalized = hasTimezone ? raw : raw.replace(' ', 'T') + 'Z';
      date = new Date(normalized);
    }
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatProfit = (profit) => {
    if (profit === null || profit === undefined) return '$0.00';
    const sign = profit >= 0 ? '+' : '';
    return `${sign}$${profit.toFixed(2)}`;
  };

  const formatNumber = (value, digits = 2) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '-';
    return Number(value).toFixed(digits);
  };

  const getConfigDisplayName = (config) => {
    if (config?.custom_name) return config.custom_name;
    if (config?.config_name) return config.config_name;
    if (config?.name) return config.name;
    return t('copyTrading.unnamedConfig');
  };

  const getConfigSecondaryName = (config) => {
    if (config?.custom_name && config?.config_name) return config.config_name;
    if (config?.custom_name && config?.name) return config.name;
    return null;
  };

  const getTranslatedPauseReason = () => {
    const reasonType = globalSettings?.copy_global_pause_reason_type;
    if (reasonType === 'insufficient_balance') {
      return t('copyTrading.pauseReasonInsufficientBalance');
    }
    if (reasonType === 'insufficient_points') {
      return t('copyTrading.pauseReasonInsufficientPoints');
    }
    if (reasonType === 'manual') {
      return t('copyTrading.pauseReasonManual');
    }
    return globalSettings?.copy_global_pause_reason || '';
  };

  const ConfigDetailView = ({
    config,
    configDetail,
    detailLoading,
    isMobile,
    onClose,
    onEditName,
    tradesData,
    tradesPageNum,
    tradesTotalPagesNum,
    tradesTotalNum,
    tradesLoadingState,
    onTradesPageChange,
  }) => {
    if (!config) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-800/50 flex items-center justify-center mb-4">
            <FiLayers className="w-8 h-8 text-slate-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-300 mb-2">{t('copyTrading.noConfigSelected')}</h3>
          <p className="text-sm text-slate-500">{t('copyTrading.selectConfigPrompt')}</p>
        </div>
      );
    }

    if (detailLoading) {
      return <CardLoading text={t('copyTrading.loadingDetail')} />;
    }

    const displayName = getConfigDisplayName(config);
    const secondaryName = getConfigSecondaryName(config);
    const traderAddresses = config.trader_addresses || [];

    return (
      <div className="flex flex-col h-full">
        {isMobile && (
          <div className="flex items-center justify-between p-4 border-b border-slate-800/50">
            <h3 className="font-semibold text-white">{t('copyTrading.configDetail')}</h3>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-800/50 transition-colors">
              <FiX className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-bold text-white truncate">{displayName}</h2>
                <button
                  onClick={() => onEditName(config)}
                  className="p-1.5 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors flex-shrink-0"
                  title={t('copyTrading.editName')}
                >
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {secondaryName && (
                <p className="text-sm text-slate-500 truncate">{secondaryName}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-slate-500">{t('copyTrading.createdAt')}: {formatDate(config.created_at)}</span>
                <span className="text-slate-700">·</span>
                <span className="text-xs text-slate-500 font-mono">ID: {config.id?.slice(0, 8)}...</span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleToggleAutoCopy(config.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  config.auto_copy_enabled
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                    : 'bg-slate-800 text-slate-400 border border-slate-700'
                }`}
              >
                {config.auto_copy_enabled ? <FiPlay className="w-3 h-3" /> : <FiPause className="w-3 h-3" />}
                {config.auto_copy_enabled ? t('copyTrading.autoOn') : t('copyTrading.autoOff')}
              </button>
            </div>
          </div>

          {/* Traders List - Collapsible */}
          <div className="mb-6 rounded-xl border border-slate-700/50 bg-slate-900/30 overflow-hidden">
            <button
              onClick={() => setTradersListOpen(!tradersListOpen)}
              className="flex items-center justify-between w-full px-4 py-3 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-sky-500/15">
                  <FiUser className="w-4 h-4 text-sky-400" />
                </div>
                <span className="text-sm font-medium text-slate-200">
                  {t('copyTrading.tradersList') || '交易员列表'}
                </span>
                <span className="text-xs text-slate-500 bg-slate-800/60 px-2 py-0.5 rounded-full">
                  {traderAddresses.length}
                </span>
              </div>
              <FiChevronDown
                className={`w-5 h-5 text-slate-500 transition-transform duration-200 ${tradersListOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {tradersListOpen && (
              <div className="px-4 py-3 border-t border-slate-700/50">
                <div className="space-y-2 max-h-48 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
                  {traderAddresses.map((address, index) => (
                    <div
                      key={address}
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-950/40 border border-slate-800/50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-600 font-mono w-5">{index + 1}</span>
                        <span className="text-sm font-mono text-slate-300">
                          {address.slice(0, 6)}...{address.slice(-4)}
                        </span>
                      </div>
                      <a
                        href={`/traders/${address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                      >
                        {t('copyTrading.viewTrader') || '查看'}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{t('copyTrading.traders')}</p>
              <p className="text-lg font-semibold text-white">{config.trader_addresses?.length || 0}</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{t('copyTrading.totalTrades')}</p>
              <p className="text-lg font-semibold text-white">{config.total_trades || 0}</p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{t('copyTrading.investmentLimit')}</p>
              <p className="text-lg font-semibold text-white">
                {config.investment_total_limit ? `$${config.investment_total_limit}` : t('copyTrading.unlimited')}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{t('copyTrading.stopLoss')}</p>
              <p className="text-lg font-semibold text-white">
                {config.stop_loss_enabled ? `$${config.stop_loss_limit || 0}` : t('copyTrading.off')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => openStrategyRiskModal(config)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors text-sm font-medium border border-slate-700/50"
            >
              <FiSettings className="w-4 h-4" />
              {t('copyTrading.riskSettings')}
            </button>
            <button
              onClick={() => handleDelete(config.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-colors text-sm font-medium border border-rose-500/30"
            >
              <FiTrash2 className="w-4 h-4" />
              {t('copyTrading.delete')}
            </button>
          </div>

          {/* Trade History Section */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <FiActivity className="w-4 h-4 text-sky-400" />
              {t('copyTrading.tradeHistory')}
              <span className="text-xs text-slate-500 font-normal">({tradesTotalNum})</span>
            </h3>
            {tradesLoadingState ? (
              <CardLoading text={false} />
            ) : tradesData.length > 0 ? (
              <div className="space-y-2">
                {tradesData.map((trade) => {
                  const isBuy = (trade.action || '').toUpperCase() === 'BUY';
                  return (
                    <div key={trade.id} className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30 hover:border-slate-600/50 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide ${
                              isBuy
                                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                                : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
                            }`}>
                              {isBuy ? t('copyTrading.actionBuy') : t('copyTrading.actionSell')}
                            </span>
                            {trade.direction && (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                trade.direction.toUpperCase() === 'YES'
                                  ? 'bg-sky-500/10 text-sky-400'
                                  : trade.direction.toUpperCase() === 'NO'
                                    ? 'bg-amber-500/10 text-amber-400'
                                    : 'bg-slate-500/10 text-slate-400'
                              }`}>
                                {trade.direction}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-medium text-white truncate">
                            {trade.market_title || trade.market_id?.slice(0, 12) + '...' || t('copyTrading.unknownMarket')}
                          </p>
                          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                            <span>{formatTimestamp(trade.executed_at)}</span>
                            {trade.source_trader_address && (
                              <>
                                <span className="text-slate-700">·</span>
                                <span className="font-mono">{trade.source_trader_address.slice(0, 6)}...{trade.source_trader_address.slice(-4)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-right flex-shrink-0">
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase">{t('copyTrading.tradeAmount')}</p>
                            <p className="text-sm font-mono text-slate-200">${formatNumber(trade.amount)}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase">{t('copyTrading.tradePrice')}</p>
                            <p className="text-sm font-mono text-sky-300">{formatNumber(trade.price, 4)}</p>
                          </div>
                          {trade.filled_size != null && (
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase">{t('copyTrading.tradeShares')}</p>
                              <p className="text-sm font-mono text-slate-300">{formatNumber(trade.filled_size, 2)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {tradesTotalPagesNum > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-3">
                    <button
                      onClick={() => onTradesPageChange(tradesPageNum - 1)}
                      disabled={tradesPageNum <= 1}
                      className="p-1.5 rounded-lg bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <FiChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-slate-500">{tradesPageNum} / {tradesTotalPagesNum}</span>
                    <button
                      onClick={() => onTradesPageChange(tradesPageNum + 1)}
                      disabled={tradesPageNum >= tradesTotalPagesNum}
                      className="p-1.5 rounded-lg bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <FiChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-8 text-center border border-dashed border-slate-800 rounded-xl">
                <FiActivity className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-sm text-slate-500">{t('copyTrading.noTrades')}</p>
                <p className="text-xs text-slate-600 mt-1">{t('copyTrading.noTradesHint')}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <PageLoading text={t('copyTrading.loadingConfigs')} />;
  }

  if (membershipRequired) {
    return (
      <section className="glass-lg w-full overflow-hidden min-h-[60vh] flex items-center justify-center text-center">
        <div className="max-w-xl px-6 py-10 flex flex-col items-center gap-4">
          <div className="text-amber-300 text-lg font-semibold">{t('membership.upgradeRequired')}</div>
          <p className="text-slate-400">{t('copyTrading.membershipRequiredDesc')}</p>
          <div className="flex items-center gap-3">
            <Button variant="primary" onClick={() => onRequireMembership?.()}>{t('membership.viewPlans')}</Button>
            <Button variant="secondary" onClick={loadConfigs}>{t('common.retry')}</Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="glass-lg w-full min-h-[calc(100vh-14rem)] flex flex-col">
      <div className="p-4 md:p-6 lg:p-8 flex flex-col gap-6 flex-1">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-slate-500 uppercase mb-1">{t('copyTrading.pageKicker')}</p>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
                <FiUsers className="text-sky-400" />
                {t('copyTrading.pageTitle')}
              </h1>
              <button 
                onClick={handleRefresh} 
                disabled={refreshing}
                className={`p-2 rounded-xl bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all active:scale-95 ${refreshing ? 'animate-spin' : ''}`}
                title={t('copyTrading.refreshTitle')}
              >
                <FiRefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-400 bg-slate-900/40 px-3 py-1.5 rounded-full border border-slate-800/50">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            <span>{t('copyTrading.systemOnline')}</span>
          </div>
        </div>

        {/* Global Risk Status - Prominent */}
        <div className={`rounded-2xl border p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 ${
          globalSettings.copy_global_paused 
            ? 'bg-amber-500/5 border-amber-500/20' 
            : 'bg-emerald-500/5 border-emerald-500/20'
        }`}>
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${
              globalSettings.copy_global_paused ? 'bg-amber-500/15' : 'bg-emerald-500/15'
            }`}>
              <FiShield className={`w-6 h-6 ${
                globalSettings.copy_global_paused ? 'text-amber-400' : 'text-emerald-400'
              }`} />
            </div>
            <div>
              <p className="text-sm font-semibold text-white mb-1">{t('copyTrading.globalRiskTitle')}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="text-slate-400">
                  {t('copyTrading.status')}: 
                  <span className={globalSettings.copy_global_paused ? 'text-amber-400 font-medium ml-1' : 'text-emerald-400 font-medium ml-1'}>
                    {globalSettings.copy_global_paused ? t('copyTrading.paused') : t('copyTrading.normal')}
                  </span>
                </span>
                <span className="text-slate-600">|</span>
                <span className="text-slate-400">
                  {t('copyTrading.lossLimit')}: 
                  <span className="text-slate-200 font-mono ml-1">
                    {globalSettings.copy_global_loss_limit ? `$${Number(globalSettings.copy_global_loss_limit).toFixed(2)}` : t('copyTrading.unlimited')}
                  </span>
                </span>
              </div>
              {globalSettings.copy_global_paused && (globalSettings.copy_global_pause_reason || globalSettings.copy_global_pause_reason_type) && (
                <p className="text-xs text-amber-400/80 mt-2">
                  {t('copyTrading.reason')}: {getTranslatedPauseReason()}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {globalSettings.copy_global_paused ? (
              <button
                onClick={handleResumeGlobal}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors text-sm font-medium border border-emerald-500/30"
              >
                <FiPlay className="w-4 h-4" />
                {t('copyTrading.resume')}
              </button>
            ) : (
              <button
                onClick={handlePauseGlobal}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors text-sm font-medium border border-amber-500/30"
              >
                <FiPause className="w-4 h-4" />
                {t('copyTrading.pauseGlobal')}
              </button>
            )}
            <button
              onClick={openGlobalRiskModal}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-800/50 text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors text-sm font-medium border border-slate-700/50"
            >
              <FiSettings className="w-4 h-4" />
              {t('copyTrading.edit')}
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-sky-500/10">
                <FiLayers className="w-5 h-5 text-sky-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_configs}</p>
                <p className="text-xs text-slate-500">{t('copyTrading.statTotalConfigs')}</p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-500/10">
                <FiCpu className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active_auto_copy}</p>
                <p className="text-xs text-slate-500">{t('copyTrading.statAutoCopy')}</p>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-slate-800/30 border border-slate-700/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-indigo-500/10">
                <FiActivity className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total_trades}</p>
                <p className="text-xs text-slate-500">{t('copyTrading.statTotalTrades')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content: Two Column Layout */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0">
          
          {/* Left: Config List */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                {t('copyTrading.configList')}
                <span className="text-xs font-mono text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{sortedConfigs.length}</span>
              </h2>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
              {sortedConfigs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-6 bg-slate-900/20 rounded-2xl border border-slate-800/50">
                  <FiLayers className="w-10 h-10 text-slate-700 mb-3" />
                  <p className="text-sm text-slate-500 text-center">{t('copyTrading.noConfigs')}</p>
                </div>
              ) : (
                sortedConfigs.map((config) => {
                  const isSelected = selectedConfig?.id === config.id;
                  const displayName = getConfigDisplayName(config);
                  const secondaryName = getConfigSecondaryName(config);
                  return (
                    <button
                      key={config.id}
                      onClick={() => handleConfigSelect(config)}
                      className={`
                        w-full text-left p-4 rounded-xl border transition-all duration-200 group
                        ${isSelected
                          ? 'bg-sky-500/10 border-sky-500/40 shadow-[0_0_20px_-5px_rgba(14,165,233,0.15)]'
                          : 'bg-slate-800/30 border-slate-700/30 hover:bg-slate-800/50 hover:border-slate-600/40'
                        }
                      `}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0 flex-1">
                          <h3 className={`font-semibold text-sm truncate ${isSelected ? 'text-white' : 'text-slate-300 group-hover:text-white'}`}>
                            {displayName}
                          </h3>
                          {secondaryName && (
                            <p className="text-[11px] text-slate-500 truncate mt-1">{secondaryName}</p>
                          )}
                        </div>
                        <div className={`flex-none w-2 h-2 rounded-full mt-1.5 ${
                          config.auto_copy_enabled 
                            ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' 
                            : 'bg-slate-600'
                        }`} />
                      </div>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="text-slate-500">
                          <span className="font-medium text-slate-300">{config.trader_addresses?.length || 0}</span> {t('copyTrading.traderUnit')}
                        </span>
                        <span className="text-slate-500">
                          <span className="font-medium text-slate-300">{config.total_trades || 0}</span> {t('copyTrading.tradeUnit')}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Config Details */}
          <div className="lg:col-span-8 rounded-2xl bg-slate-900/30 border border-slate-800/50 overflow-hidden">
            <ConfigDetailView
              config={selectedConfig}
              configDetail={selectedConfigDetail}
              detailLoading={detailLoading}
              onEditName={handleOpenCustomName}
              tradesData={trades}
              tradesPageNum={tradesPage}
              tradesTotalPagesNum={tradesTotalPages}
              tradesTotalNum={tradesTotal}
              tradesLoadingState={tradesLoading}
              onTradesPageChange={handleTradesPageChange}
            />
          </div>
        </div>
      </div>

      {/* Mobile Detail Overlay */}
      {isMobileDetailOpen && selectedConfig && (
        <div className="fixed inset-0 z-50 lg:hidden flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/60 backdrop-blur-sm">
          <div className="w-full h-[90vh] sm:h-auto sm:max-h-[85vh] bg-slate-900 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-slide-up flex flex-col">
            <ConfigDetailView
              config={selectedConfig}
              configDetail={selectedConfigDetail}
              detailLoading={detailLoading}
              isMobile={true}
              onClose={handleCloseMobileDetail}
              onEditName={handleOpenCustomName}
              tradesData={trades}
              tradesPageNum={tradesPage}
              tradesTotalPagesNum={tradesTotalPages}
              tradesTotalNum={tradesTotal}
              tradesLoadingState={tradesLoading}
              onTradesPageChange={handleTradesPageChange}
            />
          </div>
        </div>
      )}

      {/* Global Risk Modal */}
      {globalRiskOpen && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setGlobalRiskOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-700/60 bg-slate-900/95 backdrop-blur-xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-500/15 rounded-xl">
                  <FiShield className="text-indigo-400 w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-white">{t('copyTrading.globalRiskTitle')}</h3>
              </div>
              <button onClick={() => setGlobalRiskOpen(false)} className="p-2 rounded-lg hover:bg-slate-800/60 transition-colors">
                <FiX className="text-slate-400 hover:text-white" />
              </button>
            </div>
            
            <div className="bg-slate-800/30 rounded-xl p-4 border border-slate-700/30 mb-5">
              <p className="text-sm text-slate-400 leading-relaxed">
                {t('copyTrading.globalModalDesc')}
              </p>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('copyTrading.lossLimitLabel')}</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-mono">$</span>
                <input
                  value={globalLossLimitInput}
                  onChange={(e) => setGlobalLossLimitInput(e.target.value)}
                  placeholder={t('copyTrading.lossLimitPlaceholder')}
                  className="w-full rounded-xl bg-slate-950/50 border border-slate-700/50 pl-9 pr-4 py-3 text-slate-100 outline-none focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/20 transition-all font-mono placeholder:font-sans"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" size="medium" onClick={() => setGlobalRiskOpen(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" size="medium" onClick={saveGlobalRisk}>{t('copyTrading.saveSettings')}</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Strategy Risk Modal */}
      {strategyRiskOpen && selectedConfig && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setStrategyRiskOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-700/60 bg-slate-900/95 backdrop-blur-xl p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-sky-500/15 rounded-xl">
                  <FiSettings className="text-sky-400 w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-white">{t('copyTrading.strategyRiskTitle')}</h3>
              </div>
              <button onClick={() => setStrategyRiskOpen(false)} className="p-2 rounded-lg hover:bg-slate-800/60 transition-colors">
                <FiX className="text-slate-400 hover:text-white" />
              </button>
            </div>

            <div className="space-y-5">
              <div className="space-y-3">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('copyTrading.investmentLimitLabel')}</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-mono">$</span>
                  <input
                    value={strategyInvestmentLimitInput}
                    onChange={(e) => setStrategyInvestmentLimitInput(e.target.value)}
                    placeholder={t('copyTrading.investmentLimitPlaceholder')}
                    className="w-full rounded-xl bg-slate-950/50 border border-slate-700/50 pl-9 pr-4 py-3 text-slate-100 outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 transition-all font-mono placeholder:font-sans"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t('copyTrading.stopLossEnabled')}</label>
                  <button
                    onClick={() => setStrategyStopLossEnabled(!strategyStopLossEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      strategyStopLossEnabled ? 'bg-sky-500' : 'bg-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        strategyStopLossEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                {strategyStopLossEnabled && (
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-mono">$</span>
                    <input
                      value={strategyStopLossLimitInput}
                      onChange={(e) => setStrategyStopLossLimitInput(e.target.value)}
                      placeholder={t('copyTrading.stopLossLimitPlaceholder')}
                      className="w-full rounded-xl bg-slate-950/50 border border-slate-700/50 pl-9 pr-4 py-3 text-slate-100 outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 transition-all font-mono placeholder:font-sans"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="inline-flex items-center gap-2">
                    <label className="inline-flex items-center text-xs font-bold leading-none text-slate-400 uppercase tracking-wider">
                      {t('copyTrading.marketCooldownEnabled')}
                    </label>
                    <div className="relative group inline-flex items-center">
                      <FiHelpCircle className="h-4 w-4 text-slate-400" />
                      <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-md border border-slate-700/60 bg-slate-900/95 px-2.5 py-2 text-[11px] leading-relaxed text-slate-300 opacity-0 shadow-xl transition-opacity duration-150 group-hover:opacity-100">
                        {t('copyTrading.marketCooldownDesc')}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setStrategyMarketCooldownEnabled(!strategyMarketCooldownEnabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      strategyMarketCooldownEnabled ? 'bg-sky-500' : 'bg-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        strategyMarketCooldownEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                {strategyMarketCooldownEnabled && (
                  <div className="relative">
                    <input
                      value={strategyMarketCooldownMinutesInput}
                      onChange={(e) => setStrategyMarketCooldownMinutesInput(e.target.value)}
                      placeholder={t('copyTrading.marketCooldownMinutesPlaceholder')}
                      className="w-full rounded-xl bg-slate-950/50 border border-slate-700/50 px-4 py-3 pr-16 text-slate-100 outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 transition-all font-mono placeholder:font-sans"
                    />
                    <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                      {t('copyTrading.minuteUnit')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" size="medium" onClick={() => setStrategyRiskOpen(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" size="medium" onClick={saveStrategyRisk}>{t('copyTrading.saveSettings')}</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <CustomNameModal
        open={!!customNameTarget}
        onClose={() => setCustomNameTarget(null)}
        targetType="copy_config"
        targetId={customNameTarget?.id}
        currentName={customNameTarget?.custom_name || ''}
        displayName={customNameTarget?.config_name || customNameTarget?.name || customNameTarget?.id?.slice(0, 8) || ''}
        pushToast={pushToast}
        onSaved={handleCustomNameSaved}
      />
    </section>
  );
}
